export const RECOVERY_USER_SAFE_ERROR_MESSAGE =
  'Nao foi possivel enviar a preservacao agora. Verifique a conexao e tente novamente.';

const MAX_TELEMETRY_MESSAGE_LENGTH = 180;
const STATUS_IN_MESSAGE_PATTERN = /(?:status|statusCode)["']?\s*[:=]\s*["']?(\d{3})\b/i;

const coerceStatus = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const extractRecoveryHttpStatus = (
  error: unknown,
  seen = new Set<unknown>()
): number | null => {
  if (!error || typeof error !== 'object' || seen.has(error)) {
    return null;
  }
  seen.add(error);

  const raw = error as Record<string, unknown>;
  const directStatus = coerceStatus(raw.statusCode) ?? coerceStatus(raw.status);
  if (directStatus !== null) {
    return directStatus;
  }

  if (error instanceof Error) {
    const statusInMessage = error.message.match(STATUS_IN_MESSAGE_PATTERN)?.[1];
    const parsedStatus = coerceStatus(statusInMessage);
    if (parsedStatus !== null) {
      return parsedStatus;
    }
  }

  for (const key of ['originalError', 'cause', 'error']) {
    const nestedStatus = extractRecoveryHttpStatus(raw[key], seen);
    if (nestedStatus !== null) {
      return nestedStatus;
    }
  }

  return null;
};

const sanitizeTelemetryMessage = (message: string): string => {
  const sanitized = message
    .replace(/https?:\/\/[^\s"']+/gi, '[url]')
    .replace(/\b(authorization|apikey)\b\s*[:=]\s*["']?[^"',}\]\s]+/gi, '$1:[secret]')
    .replace(/\bbearer\s+[^"',}\]\s]+/gi, 'Bearer [secret]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[jwt]')
    .replace(/\b[A-Za-z0-9_-]{40,}\b/g, '[long-value]')
    .replace(/\s+/g, ' ')
    .trim();
  return sanitized.slice(0, MAX_TELEMETRY_MESSAGE_LENGTH);
};

export const buildRecoveryErrorTelemetry = (
  error: unknown
): {
  name: string;
  httpStatus: number | null;
  messageSignature: string;
} => {
  const name = error instanceof Error
    ? error.name || 'Error'
    : typeof error === 'object' && error !== null
      ? 'ErrorLike'
      : typeof error;
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';

  return {
    name,
    httpStatus: extractRecoveryHttpStatus(error),
    messageSignature: sanitizeTelemetryMessage(message || name || 'unknown'),
  };
};

const TECHNICAL_ERROR_PATTERN =
  /\b(headers|authorization|apikey|bearer|httponly|bodyused|statuscode|storage\/v1|supabase\.co|cloudflare|response|arraybuffer|undefined|function|typeerror)\b/i;

export const normalizeRecoveryUserError = (message: unknown): string => {
  if (typeof message !== 'string') {
    return RECOVERY_USER_SAFE_ERROR_MESSAGE;
  }
  const trimmed = message.trim();
  if (
    !trimmed ||
    trimmed === '[object Object]' ||
    trimmed.length > 220 ||
    TECHNICAL_ERROR_PATTERN.test(trimmed)
  ) {
    return RECOVERY_USER_SAFE_ERROR_MESSAGE;
  }
  return trimmed;
};
